import React from 'react';

import { LangCodeEnum } from 'shared/enums/LangCodeEnum';
import { Box, Typography } from '@mui/material';
import { Flag } from 'component/Flag/Flag';
import { useSessionStorage } from 'shared/hooks/useSessionStorage';
import { PREFERRED_LANGUAGE_STORAGE_KEY } from 'view/WithPreferredLanguage/WithPreferredLanguage';
import { useLanguage } from 'translations/LanguageProvider';
import { PreferredLanguagePickerItemButton } from 'component/PreferredLanguagePicker/PreferredLanguagePicker.s';
import { useLanguageName } from 'shared/hooks/useLanguageName';

export type LanguagePickerItemProps = {
  language: LangCodeEnum;
};

// Isolates a trailing "(qualifier)" so it can be rendered nowrap — prevents `word-break: break-word` from orphaning the closing bracket onto its own line (L1S-1801).
const PAREN_QUALIFIER_REGEX = /^(.+?)\s+(\(.+\))\s*$/;

export const PreferredLanguagePickerItem: React.VFC<LanguagePickerItemProps> = ({ language }) => {
  const { getLanguageName } = useLanguageName();
  const [, setSessionStoragePreferredLanguage] = useSessionStorage<LangCodeEnum>(
    PREFERRED_LANGUAGE_STORAGE_KEY
  );
  const { changeLanguage } = useLanguage();

  const onPreferredLanguageClick = () => {
    changeLanguage(language).then(() => setSessionStoragePreferredLanguage(language));
  };

  const languageName = getLanguageName(language);
  const qualifierMatch = languageName.match(PAREN_QUALIFIER_REGEX);

  return (
    <PreferredLanguagePickerItemButton
      data-testid="preferredLanguagePickerItemButton"
      onClick={onPreferredLanguageClick}
    >
      <Flag
        language={language}
        sx={{
          width: { xs: '40px', sm: '50px' },
          height: { xs: '40px', sm: '50px' },
          mb: 1,
          cursor: 'pointer',
        }}
      />
      <Typography component="span" variant="body2">
        {qualifierMatch ? (
          <>
            {qualifierMatch[1]}{' '}
            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
              {qualifierMatch[2]}
            </Box>
          </>
        ) : (
          languageName
        )}
      </Typography>
    </PreferredLanguagePickerItemButton>
  );
};
